import { UserbotCatalog } from "../../interfaces"

/**
 * Парсит массив сообщений об ошибках от бэка (zod-валидация chainTemplate при POST/PATCH)
 * в массив ValidationIssue для подсветки узлов дерева.
 *
 * Формат сообщений бэка: "chainTemplate.steps.0.kind: invalid discriminator value"
 * или "steps.1.cases.0.then.0.id: ..."
 *
 * @param messages — массив строк из error.message (400 Bad Request)
 * @returns ValidationIssue[] с извлечёнными stepId и path
 */
export const mapZodErrors = (
  messages: string[] | string,
): UserbotCatalog.ValidationIssue[] => {
  const arr = Array.isArray(messages) ? messages : [messages]
  const issues: UserbotCatalog.ValidationIssue[] = []

  for (const msg of arr) {
    // Паттерн: "<path>: <message>"
    const colonIdx = msg.indexOf(": ")
    if (colonIdx === -1) {
      issues.push({ message: msg, severity: "error" })
      continue
    }

    const rawPath = msg.slice(0, colonIdx).trim()
    const message = msg.slice(colonIdx + 2).trim()

    // Извлекаем stepId из path (напр. "chainTemplate.steps.0.cases.0.then.2.id")
    const stepId = extractStepId(rawPath)

    issues.push({
      stepId: stepId ?? undefined,
      path: rawPath,
      message,
      severity: "error",
    })
  }

  return issues
}

/**
 * Пытается извлечь id узла из zod-path.
 * Path может быть вида "steps.0.cases.0.then.2.id" — последний сегмент ".id"
 * указывает на поле id, а предпоследняя цифра — индекс узла.
 * Возвращает null, если path не содержит шагов.
 */
const extractStepId = (path: string): string | null => {
  // Убираем префикс "chainTemplate." если есть
  const cleaned = path.replace(/^chainTemplate\./, "")

  // Паттерн: steps.<idx>... и заканчивается на ".id"
  const idMatch = cleaned.match(/\.id$/)
  if (idMatch) {
    // Это ошибка в поле id — возвращаем null (id ещё не задан)
    return null
  }

  // Для ошибок в конкретном узле, path содержит индексы.
  // Мы не можем извлечь stepId (он не в path), возвращаем null.
  // Подсветка будет по path.
  return null
}
